import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Package, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router';
import type { WidgetProps } from '../../../core/types/widget.types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface InventoryStats {
  summary: { total: number; active: number; needsAttention: number; totalPurchaseValue: number };
}

const formatEuro = (v: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

export function InventoryStatsWidget({ widgetId: _ }: WidgetProps) {
  const { data, isLoading } = useQuery<InventoryStats>({
    queryKey: ['inventory-stats'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/technik/inventory/stats`, { withCredentials: true });
      return res.data.stats;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-full">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="animate-pulse bg-gray-50 rounded-2xl h-24" />
        ))}
      </div>
    );
  }

  const total = data?.summary.total ?? 0;
  const active = data?.summary.active ?? 0;
  const needsAttention = data?.summary.needsAttention ?? 0;
  const totalPurchaseValue = data?.summary.totalPurchaseValue ?? 0;

  return (
    <div className="h-full flex flex-col space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
        <div className="bg-primary-50/30 rounded-2xl p-4 border border-primary-50 flex items-center gap-4 transition-all hover:bg-primary-50/50">
          <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20">
            <Package size={20} />
          </div>
          <div className="flex-1">
            <div className="text-xl font-black text-charcoal">
              {total} <span className="text-sm font-normal text-gray-500">Geräte</span>
            </div>
            <div className="flex items-center justify-between mt-0.5">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Bestand</span>
              <span className="text-[10px] font-bold text-primary">{active} aktiv</span>
            </div>
          </div>
        </div>

        <div className="bg-gold-50/30 rounded-2xl p-4 border border-gold-50 flex items-center gap-4 transition-all hover:bg-gold-50/50">
          <div className="w-10 h-10 rounded-xl bg-gold-600 text-white flex items-center justify-center shadow-lg shadow-gold-600/20">
            <span className="text-sm font-bold">€</span>
          </div>
          <div className="flex-1">
            <div className="text-xl font-black text-charcoal">{formatEuro(totalPurchaseValue)}</div>
            <div className="flex items-center justify-between mt-0.5">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Gesamtkaufwert</span>
            </div>
          </div>
        </div>
      </div>

      {needsAttention > 0 && (
        <div className="p-3 bg-amber-50 rounded-xl flex items-start gap-3 border border-amber-100/50">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-charcoal">
                {needsAttention} {needsAttention === 1 ? 'Gerät' : 'Geräte'} defekt oder in Wartung
              </p>
              <Link
                to="/technik/inventory"
                className="text-[10px] font-bold text-primary hover:text-primary-700 bg-white px-2 py-1 rounded-sm border border-primary-100 transition-colors"
              >
                Zum Inventar →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
